import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Heart, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { unsubscribeSubscriber, getSubscriberByToken } from '../firebase/subscribers';

export function UnsubscribePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [subscriber, setSubscriber] = useState(null);

  useEffect(() => {
    handleUnsubscribe();
  }, [token]);

  const handleUnsubscribe = async () => {
    if (!token) {
      setError('Invalid unsubscribe link');
      setLoading(false);
      return;
    }

    try {
      // First, get subscriber info to show their email
      const subscriberData = await getSubscriberByToken(token);
      
      if (!subscriberData) {
        setError('Invalid or expired unsubscribe link. Please contact us if you need help.');
        setLoading(false);
        return;
      }

      setSubscriber(subscriberData);

      // If already unsubscribed, show success message
      if (subscriberData.status === 'unsubscribed') {
        setSuccess(true);
        setLoading(false);
        return;
      }

      // Unsubscribe
      const result = await unsubscribeSubscriber(token);
      
      if (result.success) {
        setSuccess(true);
      } else {
        setError(result.error || 'Failed to unsubscribe. Please try again or contact us.');
      }
    } catch (err) {
      setError('An error occurred. Please try again or contact us for help.');
      console.error('Unsubscribe error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fdfbf7] via-[#fae1e5]/20 to-[#f3e6d5]/20 flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        {loading ? (
          <div className="bg-white rounded-2xl shadow-xl border border-[#eaddcf] p-12 text-center">
            <Loader2 className="w-12 h-12 text-[#c5a059] animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Processing your request...</p>
          </div>
        ) : success ? (
          <div className="bg-white rounded-2xl shadow-xl border border-[#eaddcf] p-12 text-center">
            <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-serif text-gray-800 mb-4">
              You're Unsubscribed
            </h1>
            <p className="text-gray-600 mb-6 leading-relaxed">
              {subscriber?.email && (
                <>
                  <strong>{subscriber.email}</strong> has been successfully removed from our mailing list.
                </>
              )}
              {!subscriber?.email && (
                <>You have been successfully removed from our mailing list.</>
              )}
            </p>
            <p className="text-sm text-gray-500 mb-8">
              You won't receive any more emails from us. We're sorry to see you go!
            </p>
            <button
              onClick={() => navigate('/')}
              className="px-8 py-3 bg-[#c5a059] text-white rounded-full font-medium hover:bg-[#b08e4d] transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Return to Home
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-xl border border-[#eaddcf] p-12 text-center">
            <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-3xl font-serif text-gray-800 mb-4">
              Unable to Unsubscribe
            </h1>
            <p className="text-gray-600 mb-6 leading-relaxed">
              {error || 'Something went wrong. Please try again or contact us for help.'}
            </p>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/')}
                className="w-full px-8 py-3 bg-[#c5a059] text-white rounded-full font-medium hover:bg-[#b08e4d] transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Return to Home
              </button>
              <a
                href="mailto:Fabiha@freespiritfabiha.com"
                className="block w-full px-8 py-3 border-2 border-[#d48c95] text-[#d48c95] rounded-full font-medium hover:bg-[#fae1e5] transition-all duration-300"
              >
                Contact Us
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}









